import React, { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://fjswgwdweohwejbrmiil.supabase.co/functions/v1/get-dashboard-data';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch(API_URL, {
          timeout: 60000,
          mode: 'cors',
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data.error) throw new Error(data.error);
        setDashboardData(data);
      } catch (err) {
        setError(err.message);
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) return <div className="text-white text-center p-4">Loading...</div>;
  if (error) return <div className="text-red-500 text-center p-4">Error: {error}</div>;

  // Get emoji flag from country code
  const getCountryFlag = (code) => {
    if (!code) return '';
    // Convert country code to flag emoji
    const codePoints = code
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  // First, update number formatting for all monetary values:
  const formatNumber = (num) => {
    return (Math.round((num || 0) * 100) / 100).toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  return (
    <div className="min-h-screen bg-futurecash-dark text-white">
      <div className="max-w-full mx-auto p-4 lg:p-5">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-center mb-8 lg:mb-16 mt-4 lg:mt-8">
          <h1 className="text-4xl lg:text-6xl font-bold tracking-tight mb-4 lg:mb-0" style={{ fontWeight: 650, lineHeight: 1 }}>
            <span className="text-white">FUTURECASH</span>
          </h1>
          <button 
            onClick={() => window.open('https://send.app', '_blank')}
            className="w-full lg:w-auto bg-futurecash-green text-black px-6 py-2 rounded-sm text-base font-bold"
          >
            START SENDING
          </button>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* First row - PASSKEYS, USDC VOLUME, SEND */}
          <div className="bg-futurecash-card p-6 rounded-lg">
            <p className="text-sm uppercase text-gray-400 mb-3">PASSKEYS</p>
            <p className="text-4xl font-bold overflow-hidden">{(dashboardData?.passkeys || 0).toLocaleString()}</p>
          </div>
          
          <div className="bg-futurecash-card p-6 rounded-lg">
            <p className="text-sm uppercase text-gray-400 mb-3">USDC VOLUME (24H)</p>
            <p className="text-4xl font-bold overflow-hidden">
              ${formatNumber(dashboardData?.usdc_volume)}
            </p>
          </div>
          
          <div className="bg-futurecash-card p-6 rounded-lg">
            <p className="text-sm uppercase text-gray-400 mb-3">SENDS (24H)</p>
            <p className="text-4xl font-bold overflow-hidden">
              {Math.round(dashboardData?.send_volume || 0).toLocaleString()}
            </p>
          </div>

          {/* Daily Active Users - right column */}
          <div className="bg-futurecash-card p-6 rounded-lg h-full">
            <p className="text-sm uppercase text-gray-400 mb-4">DAILY ACTIVE USERS (24H)</p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Senders</span>
                <span className="text-xl font-bold">{(dashboardData?.daily_active_senders || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Receivers</span>
                <span className="text-xl font-bold">{(dashboardData?.daily_active_receivers || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center border-t border-gray-800/50 mt-3 pt-3">
                <span className="text-gray-400">Unique</span>
                <span className="text-xl font-bold">{(dashboardData?.daily_active_transfers || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Second row - SENDTAGS, CROSS BORDER USDC, SEND VOLUME */}
          <div className="bg-futurecash-card p-6 rounded-lg">
            <p className="text-sm uppercase text-gray-400 mb-3">SENDTAGS</p>
            <p className="text-4xl font-bold overflow-hidden">{(dashboardData?.sendtags || 0).toLocaleString()}</p>
          </div>
          
          <div className="bg-futurecash-card p-6 rounded-lg">
            <p className="text-sm uppercase text-gray-400 mb-3">CROSS BORDER USDC (24H)</p>
            <p className="text-4xl font-bold overflow-hidden">${formatNumber(dashboardData?.cross_border_volume)}</p>
          </div>
          
          <div className="bg-futurecash-card p-6 rounded-lg">
            <p className="text-sm uppercase text-gray-400 mb-3">SEND VOLUME (24HR)</p>
            <p className="text-4xl font-bold overflow-hidden">
              {Math.round(dashboardData?.send_volume || 0).toLocaleString()}
            </p>
          </div>

          {/* Top Sending Countries - right column */}
          <div className="bg-futurecash-card p-6 rounded-lg lg:row-span-2">
            <p className="text-sm uppercase text-gray-400 mb-6">
              TOP SENDING COUNTRIES [TXNS]
            </p>
            <div className="flex flex-col justify-between space-y-4">
              {(dashboardData?.country_stats || []).map((country, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{getCountryFlag(country.code)}</span>
                    <span className="text-base">{country.name}</span>
                  </div>
                  <span className="text-futurecash-green text-base">
                    {country.tx_count.toLocaleString()} TXNs
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-futurecash-card p-6 rounded-lg">
            <p className="text-sm uppercase text-gray-400 mb-4">
              SENDTAG REVENUE
            </p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-lg">Send</span>
                <span className="text-lg">${formatNumber(dashboardData?.sendtag_revenue)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg">Affiliates</span>
                <span className="text-lg">${formatNumber(dashboardData?.sendtag_referral_payouts)}</span>
              </div>
            </div>
          </div>

          <div className="bg-futurecash-card p-6 rounded-lg col-span-1 lg:col-span-2">
            <div className="space-y-5">
              <div>
                <p className="text-sm uppercase text-gray-400 mb-3">NEW SENDTAGS</p>
                <div className="flex flex-wrap gap-2">
                  {(dashboardData?.new_sendtags || []).slice(0, 7).map((tag, index) => (
                    <div key={index} className="bg-futurecash-card-dark rounded px-3 py-1 text-futurecash-green text-sm lowercase">
                      /{tag.name}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm uppercase text-gray-400 mb-3">NEW AFFILIATES</p>
                <div className="flex flex-wrap gap-2">
                  {(dashboardData?.new_affiliates || []).slice(0, 7).map((affiliate, index) => (
                    <div key={index} className="bg-futurecash-card-dark rounded px-3 py-1 text-futurecash-green text-sm lowercase">
                      /{affiliate.name}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mt-8">
          <div className="w-full border-t border-gray-800 mb-4"></div>
          <div className="flex justify-end">
            <p className="text-futurecash-green text-xl" style={{ fontFamily: 'sans-serif', fontWeight: 'bolder' }}>/send</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;